import { inject, Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { PaginationResult } from '../../core/db/firestore.models';
import { WalletDbService } from './wallet-db.service';
import { cleanedWallet } from './wallet.helpers';
import { Wallet, WalletTransaction } from './wallet.models';

@Injectable({ providedIn: 'root' })
export class WalletService {
  wallet: Wallet;
  applyWallet$ = new Subject<void | boolean>();

  private walletDbService = inject(WalletDbService);

  /**
   * Get the wallet of the user provided by its userId.
   * If the wallet does not exist, it returns a cleaned wallet with default values.
   * @param userId The ID of the user whose wallet is to be retrieved.
   * @returns An Observable of the user's wallet, or a cleaned wallet if not found.
   */
  getWalletByUserId$(userId: string): Observable<Wallet> {
    return this.walletDbService.get$(userId).pipe(
      map(wallet => wallet ?? cleanedWallet())
    );
  }

  /**
   * Get the total count of wallet transactions for a given user ID.
   * @param userId ID of the user whose wallet transactions count is to be retrieved.
   * @returns The total count of wallet transactions for the specified user. If there are no transactions, it returns 0.
   */
  getTotalWalletTransactionsCount(userId: string): Promise<number> {
    return this.walletDbService.countDocs({}, { subPath: `${userId}/wallettransactions` });
  }

  /**
   * Get a paginated list of wallet transactions for the specified user ID
   * @param id User ID.
   * @param pageSize Number of transactions to retrieve per page.
   * @param startAfterDoc Optional snapshot cursor for next page.
   * @returns Observable with paginated wallet transactions.
   */
  getWalletTransactions$(id: string, pageSize: number, startAfterDoc = null): Observable<PaginationResult<WalletTransaction>> {
    return this.walletDbService.paginate$<WalletTransaction>(
      {
        orderBy: { createdAt: 'desc' },
        limit: pageSize ?? 100,
        ...(startAfterDoc ? { startAfter: startAfterDoc } : {})
      },
      { subPath: `${id}/wallettransactions` }
    );
  }

  // Método para actualizar el estado actual de la wallet y notificar a los suscriptores
  updateCurrentWallet(wallet: Wallet | null, shouldNotify = false): void {
    this.wallet = wallet ?? cleanedWallet();
    if (shouldNotify) {
      this.applyWallet$.next();
    }
  }
}
